import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import ModifyFixedPriceRow from './ModifyFixedPriceRow'
import ViewFixedPriceRow from './ViewFixedPriceRow'
import { FixedPriceMutationDto, FixedPriceOverviewDto, FixedPriceType } from '../../../config/registrationConfig'
import RegistrationList, { registrationListColumns, RegistrationListProps } from '../Registration/RegistrationList'
import { IColumn } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'
import { parseNumber } from '../../../helpers/numberHelper'
import useVatTypes from '../../../hooks/useVatTypes'
import { getOppositePrice } from '../../../helpers/priceHelper'
import { PricePrecedence } from '../../../config/finance/financeConfig'
import { VatType } from '../../../config/i18nConfig'

export type FixedPriceListProps = RegistrationListProps<FixedPriceOverviewDto> & {
    useLongDescription?: boolean
    useCode?: boolean
}

const getColumns = (translate: (key: string) => string, useType: boolean, useName: boolean): IColumn[] =>
    [
        registrationListColumns.user(),
        registrationListColumns.date(),
        useType && {
            key: 'type',
            fieldName: 'type',
            name: translate('base.table.header.type'),
            minWidth: 120,
            maxWidth: useName ? 120 : 200,
        },
        {
            key: 'description',
            fieldName: 'description',
            name: translate('base.table.header.description'),
            minWidth: 100,
            isResizable: true,
            isRowHeader: true,
        },
        registrationListColumns.price('rate', translate('base.table.header.rate'), false),
        {
            key: 'vat',
            fieldName: 'vatType',
            name: translate('base.table.header.vat'),
            minWidth: 96,
            maxWidth: 96,
        },
        registrationListColumns.price('total', translate('base.table.header.total'), true),
        registrationListColumns.invoice(translate),
        registrationListColumns.actions(),
    ].filter(Boolean)

const getValidationSchema = (fixedPriceTypes: FixedPriceType[], translate: (key: string) => string) =>
    Yup.object().shape({
        userId: Yup.string().trim().required(translate('file.pivot.registration.form.userid.empty')),
        date: Yup.string().trim().required(translate('file.pivot.registration.form.date.empty')),
        type: Yup.string()
            .trim()
            .when({
                is: () => fixedPriceTypes.length > 0,
                then: schema => schema.required(translate('file.pivot.registration.form.type.empty')),
                otherwise: schema => schema.nullable(),
            })
            .test(
                'is-valid-type',
                translate('file.pivot.registration.form.type.empty'),
                value => fixedPriceTypes.length === 0 || value !== undefined,
            ),
        description: Yup.string().trim().required(translate('file.pivot.registration.form.description.empty')),
        vatType: Yup.string().trim().required(translate('file.pivot.registration.form.vattype.empty')),
    })

export const getEffectiveFixedPriceRate = (
    initialSubtotalRate: string,
    initialTotalRate: string,
    fixedPriceType: FixedPriceType,
    vatType: VatType,
    toPrecedence: PricePrecedence,
): string => {
    let fromPrecedence = PricePrecedence.Subtotal
    let initialRate = parseNumber(initialSubtotalRate, 'en')
    if (fixedPriceType?.includingVat) {
        fromPrecedence = PricePrecedence.Total
        initialRate = parseNumber(initialTotalRate, 'en')
    }
    initialRate = initialRate || parseNumber(fixedPriceType?.rate, 'en') || 0
    if (fromPrecedence !== toPrecedence) {
        return `${getOppositePrice(initialRate, vatType, fromPrecedence)}`
    } else {
        return `${initialRate}`
    }
}

export default function FixedPriceList(props: FixedPriceListProps): ReactElement {
    const { fixedPriceTypes } = useFixedPriceTypes()
    const { t: translate } = useTranslation()
    const { getVatType } = useVatTypes()

    //When there is no long_description set use the name as name and the description as description.
    //When there is a long_description set use the description as name and the long_description as description.
    const useName = useMemo<boolean>(() => fixedPriceTypes.filter(type => !!type.longDescription).length === 0, [fixedPriceTypes])

    const columns = useMemo(() => getColumns(translate, fixedPriceTypes.length > 0, useName), [fixedPriceTypes, useName])

    return (
        <RegistrationList<FixedPriceOverviewDto, FixedPriceMutationDto>
            detailsListProps={{ columns }}
            {...props}
            listProps={{ ...props.listProps }}
            baseUrl="/registration/fixed-price"
            initialValues={{
                userId: props.listProps.file.owner.id,
                type: fixedPriceTypes?.length === 1 ? fixedPriceTypes[0].id : undefined,
                vatType: fixedPriceTypes?.length === 1 ? fixedPriceTypes[0].vatType : props.listProps.file.vatType,
                description: fixedPriceTypes?.length === 1 ? (fixedPriceTypes[0].longDescription ?? fixedPriceTypes[0].description) : undefined,
                rate:
                    fixedPriceTypes?.length === 1
                        ? getEffectiveFixedPriceRate(
                              undefined,
                              undefined,
                              fixedPriceTypes[0],
                              getVatType(fixedPriceTypes[0].vatType),
                              PricePrecedence.Subtotal,
                          )
                        : '0.00',
                rateTotal:
                    fixedPriceTypes?.length === 1
                        ? getEffectiveFixedPriceRate(
                              undefined,
                              undefined,
                              fixedPriceTypes[0],
                              getVatType(fixedPriceTypes[0].vatType),
                              PricePrecedence.Total,
                          )
                        : '0.00',
            }}
            mapItem={(item: FixedPriceOverviewDto) => {
                return {
                    type: item.type,
                    vatType: item.vatType,
                    rate: item.rate.amount,
                    rateTotal: `${getOppositePrice(parseNumber(item.rate?.amount, 'en'), getVatType(item.vatType), PricePrecedence.Subtotal)}`,
                }
            }}
            viewRow={ViewFixedPriceRow}
            modifyRow={ModifyFixedPriceRow}
            validationSchema={getValidationSchema(fixedPriceTypes, translate)}
        />
    )
}
